export const range = (from: number, to: number): Array<number> => {
    const min = Math.min(from, to);
    const numbers = Array.from(Array(Math.abs(from - to) + 1).keys()).map((x) => x + min);
    if (from > to) {
        return numbers.reverse();
    }
    return numbers;
};

export const isNumeric = (char: any): char is number => {
    if (char == null) return false;
    if (typeof char === 'string' && char.trim() === '') return false;

    return !isNaN(char);
};

export const seededRandom = (seed: number): number => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
};

export const round = (x: number): number => {
    return Math.round(x * 1e12) / 1e12;
};

const isLetter = (str: string | undefined) => {
    return !!str && str.length === 1 && str.match(/[a-z]/i);
};

export const unitStringToNumber = (source: string): number => {
    const lowerSource = source.toLowerCase().replaceAll(',', '');
    const float = parseFloat(lowerSource);

    if (lowerSource.slice(-8) === 'thousand') return float * 1e3;
    if (lowerSource.slice(-7) === 'million') return float * 1e6;
    if (lowerSource.slice(-7) === 'billion') return float * 1e9;
    if (lowerSource.slice(-8) === 'trillion') return float * 1e12;

    if (isLetter(lowerSource.slice(-2)[0])) {
        return float;
    }

    if (lowerSource.slice(-1)[0] === 'k') return float * 1e3;
    if (lowerSource.slice(-1)[0] === 'm') return float * 1e6;
    if (lowerSource.slice(-1)[0] === 'b') return float * 1e9;
    if (lowerSource.slice(-1)[0] === 't') return float * 1e12;

    return float;
};

// Generate a random number with specific amount of digits
export const randomDigits = (digits = 12): number => {
    return Math.floor(Math.random() * Math.pow(10, digits));
};

export interface FormatPercentOptions {
    decimals?: number;
    fallback?: string;
    lowThreshold?: number;
    includeSuffix?: boolean;
}

/**
 * Formats a percentage value to a fixed-decimal string with a trailing `%`.
 *
 * Accepts `number`, numeric strings (with or without a trailing `%`), or
 * nullish/empty inputs. When the input can't be parsed to a finite number,
 * `fallback` is returned so callers can conditionally omit the segment.
 *
 * Options:
 * - `decimals` (default `1`): fractional digits passed to `toFixed`.
 * - `fallback` (default `''`): returned when the input is nullish/unparseable.
 * - `lowThreshold`: when provided, any finite numeric strictly less than the
 *   threshold renders as `<${lowThreshold}%` (preserves the UI's clamp for
 *   tiny vote shares). Matches the existing `<1%` behavior used by ranked-
 *   choice voting displays.
 * - `includeSuffix` (default `true`): when `false`, omits the trailing `%`
 *   (useful when a surrounding component renders `%` itself).
 */
export function formatPercent(value: string | number | null | undefined, options: FormatPercentOptions = {}): string {
    const { decimals = 1, fallback = '', lowThreshold, includeSuffix = true } = options;
    if (value == null || value === '') return fallback;
    const numeric = typeof value === 'number' ? value : parseFloat(String(value).replace('%', ''));
    if (!Number.isFinite(numeric)) return fallback;
    const suffix = includeSuffix ? '%' : '';
    if (lowThreshold != null && numeric < lowThreshold) {
        return `<${lowThreshold}${suffix}`;
    }
    return `${numeric.toFixed(decimals)}${suffix}`;
}
